import React from 'react';
import { SpacingStack } from '@withjoy/joykit';
import { SummaryItem } from '@shared/components/SummaryCard/SummaryItem';
import type { SurveyComponent, SurveyConfig } from '@shared/components/Survey';

type SurveyResponsesSummaryProps = {
  formData: SurveyConfig;
  formValues: Record<string, string>;
};

/**
 * Gets the display value for a survey component based on its type.
 * For radio/checkbox components, it resolves the option value to its label.
 * For other components (input, textarea), it returns the value as-is.
 */
const getDisplayValue = (component: SurveyComponent, rawValue: string | string[]): string => {
  switch (component.type) {
    case 'radio': {
      // For radio, rawValue should be a single value
      const selectedOption = component.options.find(opt => opt.value === rawValue);
      return selectedOption?.label || String(rawValue);
    }
    case 'checkbox': {
      // For checkbox, rawValue could be an array of values
      const values = Array.isArray(rawValue) ? rawValue : [rawValue];
      const labels = values
        .map(val => {
          const selectedOption = component.options.find(opt => opt.value === val);
          return selectedOption?.label || val;
        })
        .filter(Boolean);
      return labels.join(', ');
    }
    case 'input':
    case 'textarea':
    default:
      return Array.isArray(rawValue) ? rawValue.join(', ') : rawValue;
  }
};

export const SurveyResponsesSummary = ({ formData, formValues }: SurveyResponsesSummaryProps) => {
  return (
    <SpacingStack gap="12px">
      {formData.steps.flatMap(step =>
        step.components
          .filter(component => formValues[component.id])
          .map(component => (
            <SummaryItem
              key={component.id}
              label={component.label || step.title || component.id}
              value={getDisplayValue(component, formValues[component.id])}
            />
          )),
      )}
    </SpacingStack>
  );
};
